import {
  computed,
  readonly,
  ref,
  useContext,
  useRoute
} from "@nuxtjs/composition-api";
import { addItemCommand } from "./commands/addItemCommand";
import { applyCouponCommand } from "./commands/applyCouponCommand";
import { loadCartCommand } from "./commands/loadCartCommand";
import { loadTotalQtyCommand } from "./commands/loadTotalQtyCommand";
import { removeCouponCommand } from "./commands/removeCouponCommand";
import { removeItemCommand } from "./commands/removeItemCommand";
import { updateItemQtyCommand } from "./commands/updateItemQtyCommand";
import { Logger } from "@wemade-vsf/core";
import { useCartStore } from "../../stores/cart";
import { useWishlist } from "@wemade-vsf/wishlist";
import { useEvent, useUiNotification } from "@wemade-vsf/composables";
function useCart() {
  const loading = ref(false);
  const error = ref({
    addItem: null,
    removeItem: null,
    updateItemQty: null,
    load: null,
    clear: null,
    applyCoupon: null,
    removeCoupon: null,
    loadTotalQty: null,
    addCustomOrderFields: null
  });
  const { app } = useContext();
  const context = app.$vsf;
  const route = useRoute();
  const cartStore = useCartStore();
  const cart = computed(() => cartStore.cart);
  const apiState = context.$magento.config.state;
  const { send: sendNotification } = useUiNotification();
  const { loading: wishlistLoading, afterAddingWishlistItemToCart } = useWishlist();
  const setCart = (newCart) => {
    Logger.debug("useCart.setCart", newCart);
    cartStore.$patch((state) => {
      state.cart = newCart;
    });
  };
  const isInCart = (product) => {
    var _a, _b;
    return !!((_b = (_a = cart.value) == null ? void 0 : _a.items) == null ? void 0 : _b.find((cartItem) => {
      var _a2;
      return ((_a2 = cartItem == null ? void 0 : cartItem.product) == null ? void 0 : _a2.uid) === product.uid;
    }));
  };
  const load = async ({ customQuery = {}, customHeaders = {}, realCart = false } = { customQuery: { cart: "cart" }, customHeaders: {} }) => {
    Logger.debug("useCart.load");
    try {
      loading.value = true;
      const loadedCart = await loadCartCommand.execute(context, { customQuery, customHeaders, realCart });
      cartStore.$patch((state) => {
        state.cart = loadedCart;
      });
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error("useCart/load", err);
    } finally {
      loading.value = false;
    }
  };
  const clear = async ({ customQuery, customHeaders } = { customQuery: { cart: "cart" }, customHeaders: {} }) => {
    Logger.debug("useCart.clear");
    try {
      loading.value = true;
      context.$magento.config.state.removeCartId();
      const loadedCart = await loadCartCommand.execute(context, { customQuery, customHeaders });
      cartStore.$patch((state) => {
        state.cart = loadedCart;
      });
    } catch (err) {
      error.value.clear = err;
      Logger.error("useCart/clear", err);
    } finally {
      loading.value = false;
    }
  };
  const loadTotalQty = async (params) => {
    Logger.debug("useCart.loadTotalQty");
    try {
      loading.value = true;
      const totalQuantity = await loadTotalQtyCommand.execute(context, params);
      cartStore.$patch((state) => {
        state.cart.total_quantity = totalQuantity;
      });
    } catch (err) {
      error.value.loadTotalQty = err;
      Logger.error("useCart/loadTotalQty", err);
    } finally {
      loading.value = false;
    }
  };
  const addItem = async ({
    product,
    customOptions,
    quantity,
    productConfiguration,
    customQuery,
    customHeaders
  }) => {
    var _a;
    Logger.debug("useCart.addItem", { product, quantity });
    try {
      loading.value = true;
      if (!apiState.getCartId()) {
        await load({ realCart: true });
      }
      const updatedCart = await addItemCommand.execute(context, {
        currentCart: cart.value,
        product,
        customOptions,
        quantity,
        productConfiguration,
        customQuery,
        customHeaders
      });
      useEvent("cart:addItem", {
        product,
        productConfiguration,
        quantity
      });
      sendNotification({
        id: Symbol("cart-add-item"),
        icon: "check",
        type: "success",
        persist: false,
        message: app.i18n.t("Product added to cart")
      });
      error.value.addItem = null;
      cartStore.$patch((state) => {
        state.cart = updatedCart;
      });
    } catch (err) {
      error.value.addItem = err;
      Logger.error("useCart/addItem", err);
    } finally {
      if (!wishlistLoading.value && ((_a = route.value.query) == null ? void 0 : _a.wishlist)) {
        afterAddingWishlistItemToCart({
          product,
          cartError: error.value.addItem
        });
      }
      loading.value = false;
    }
  };
  const removeItem = async ({ product, customQuery, customHeaders }) => {
    Logger.debug("useCart.removeItem", { product });
    try {
      loading.value = true;
      const updatedCart = await removeItemCommand.execute(context, {
        currentCart: cart.value,
        product,
        customQuery,
        customHeaders
      });
      useEvent("cart:removeItem", { product });
      error.value.removeItem = null;
      cartStore.$patch((state) => {
        state.cart = updatedCart;
      });
    } catch (err) {
      error.value.removeItem = err;
      Logger.error("useCart/removeItem", err);
    } finally {
      loading.value = false;
    }
  };
  const updateItemQty = async ({ product, quantity, customQuery = { updateCartItems: "updateCartItems" } }) => {
    Logger.debug("useCart.updateItemQty", {
      product,
      quantity
    });
    if (quantity && quantity > 0) {
      try {
        loading.value = true;
        const prevCartItem = cart.value.items.find((cartItem) => cartItem.uid === product.uid);
        const updatedCart = await updateItemQtyCommand.execute(context, {
          currentCart: cart.value,
          product,
          quantity,
          customQuery
        });
        useEvent("cart:updateQty", {
          product,
          quantity,
          previousQty: (prevCartItem == null ? void 0 : prevCartItem.quantity) || 0
        });
        error.value.updateItemQty = null;
        cartStore.$patch((state) => {
          state.cart = updatedCart;
        });
      } catch (err) {
        error.value.updateItemQty = err;
        Logger.error("useCart/updateItemQty", err);
      } finally {
        loading.value = false;
      }
    }
  };
  const handleCoupon = async (couponCode = null, customQuery = null) => {
    const variables = {
      currentCart: cart.value,
      customQuery,
      couponCode
    };
    const { updatedCart, errors } = couponCode ? await applyCouponCommand.execute(context, variables) : await removeCouponCommand.execute(context, variables);
    if (errors) {
      throw errors[0];
    }
    if (updatedCart) {
      cartStore.$patch((state) => {
        state.cart = updatedCart;
      });
    }
  };
  const applyCoupon = async ({ couponCode, customQuery }) => {
    Logger.debug("useCart.applyCoupon");
    try {
      loading.value = true;
      await handleCoupon(couponCode, customQuery);
      error.value.applyCoupon = null;
    } catch (err) {
      error.value.applyCoupon = err;
      Logger.error("useCart/applyCoupon", err);
    } finally {
      loading.value = false;
    }
  };
  const removeCoupon = async ({ customQuery }) => {
    Logger.debug("useCart.removeCoupon");
    try {
      loading.value = true;
      await handleCoupon(null, customQuery);
      error.value.applyCoupon = null;
    } catch (err) {
      error.value.removeCoupon = err;
      Logger.error("useCart/removeCoupon", err);
    } finally {
      loading.value = false;
    }
  };
  const handleCustomOrderFields = async (params) => {
    var _a;
    const variables = {
      cart_id: cart.value.id,
      ...params
    };
    const { data } = await context.$magento.api.addCustomFieldsToCart(variables);
    Logger.debug("[Result]:", { data });
    const fields = ((_a = data == null ? void 0 : data.WmAddCustomFieldsToCart) == null ? void 0 : _a.wm_customer_order_fields) || null;
    if (!fields) {
      throw new Error("Unable to set order fields");
    }
    cartStore.$patch((state) => {
      state.cart = {
        ...state.cart,
        wm_customer_order_fields: fields
      };
    });
  };
  const addCustomOrderFields = async (params) => {
    Logger.debug("useCart.addCustomOrderFields");
    try {
      loading.value = true;
      await handleCustomOrderFields(params);
      error.value.addCustomOrderFields = null;
    } catch (err) {
      error.value.addCustomOrderFields = err;
      Logger.error("useCart/addCustomOrderFields", err);
    } finally {
      loading.value = false;
    }
  };
  const canAddToCart = (product, qty = 1) => {
    var _a, _b;
    if ((product == null ? void 0 : product.__typename) === "ConfigurableProduct") {
      return !!((_b = (_a = product == null ? void 0 : product.configurable_product_options_selection) == null ? void 0 : _a.variant) == null ? void 0 : _b.uid);
    }
    const inStock = (product == null ? void 0 : product.stock_status) || "";
    const stockLeft = (product == null ? void 0 : product.only_x_left_in_stock) === null ? true : qty <= (product == null ? void 0 : product.only_x_left_in_stock);
    return inStock !== "OUT_OF_STOCK" && stockLeft;
  };
  return {
    setCart,
    cart,
    loadTotalQty,
    isInCart,
    addItem,
    load,
    removeItem,
    clear,
    updateItemQty,
    applyCoupon,
    removeCoupon,
    canAddToCart,
    addCustomOrderFields,
    loading: readonly(loading),
    error: readonly(error)
  };
}
var useCart_default = useCart;
export * from "./useCart";
export {
  useCart_default as default,
  useCart
};
